import React, { useState, useEffect, useMemo } from 'react';
import { ABTest, ABTestHistory } from '../../../types/template';
import { ComparisonResult } from '../../../utils/statisticalAnalysis';

interface InsightsEngineProps {
  test: ABTest;
  testHistory?: ABTestHistory;
  comparisonResult?: ComparisonResult;
}

interface Insight {
  id: string;
  type: 'performance' | 'statistical' | 'segment' | 'temporal' | 'business' | 'pattern' | 'recommendation';
  title: string;
  description: string;
  confidence: number;
  actionable: boolean;
  impact: 'low' | 'medium' | 'high';
  category: 'winner_analysis' | 'test_optimization' | 'future_strategy' | 'risk_assessment';
  data?: any;
  recommendations?: string[];
  relatedTests?: string[];
}

interface PatternAnalysis {
  winningElements: Array<{
    element: string;
    successRate: number;
    averageImprovement: number;
    frequency: number;
  }>;
  failingElements: Array<{
    element: string;
    failureRate: number;
    averageDecline: number;
    frequency: number;
  }>;
  seasonalTrends: Array<{
    period: string;
    performance: number;
    confidence: number;
  }>;
  audienceInsights: Array<{
    segment: string;
    preference: string;
    strength: number;
  }>;
}

const InsightsEngine: React.FC<InsightsEngineProps> = ({
  test,
  testHistory,
  comparisonResult
}) => {
  const [insights, setInsights] = useState<Insight[]>([]);
  const [patternAnalysis, setPatternAnalysis] = useState<PatternAnalysis | null>(null);
  const [selectedCategory, setSelectedCategory] = useState<Insight['category'] | 'all'>('all');
  const [isAnalyzing, setIsAnalyzing] = useState(false);

  // Generate insights when data changes
  useEffect(() => {
    generateAllInsights();
  }, [test, testHistory, comparisonResult]);

  const generateAllInsights = async () => {
    setIsAnalyzing(true);
    
    try {
      const allInsights: Insight[] = [];

      // Generate statistical insights if comparison result is available
      if (comparisonResult) {
        const statisticalInsights = generateStatisticalInsights(comparisonResult);
        allInsights.push(...statisticalInsights);
      }

      // Generate test-specific insights
      const testInsights = generateTestInsights(test);
      allInsights.push(...testInsights);

      // Generate historical pattern insights
      if (testHistory) {
        const historicalInsights = generateHistoricalInsights(testHistory);
        allInsights.push(...historicalInsights);
        
        // Generate pattern analysis
        const patterns = analyzePatterns(testHistory);
        setPatternAnalysis(patterns);
      }

      // Generate business impact insights
      const businessInsights = generateBusinessInsights(test, comparisonResult);
      allInsights.push(...businessInsights);

      // Sort by confidence and impact
      const sortedInsights = allInsights.sort((a, b) => {
        const aScore = a.confidence * (a.impact === 'high' ? 3 : a.impact === 'medium' ? 2 : 1);
        const bScore = b.confidence * (b.impact === 'high' ? 3 : b.impact === 'medium' ? 2 : 1);
        return bScore - aScore;
      });

      setInsights(sortedInsights);
    } catch (error) {
      console.error('Error generating insights:', error);
    } finally {
      setIsAnalyzing(false);
    }
  };

  const generateStatisticalInsights = (comparison: ComparisonResult): Insight[] => {
    const insights: Insight[] = [];

    // Statistical significance insight
    if (comparison.statistical.isSignificant) {
      insights.push({
        id: 'statistical-significance',
        type: 'statistical',
        title: 'Statistical Significance Achieved',
        description: `Results show statistical significance with p-value of ${comparison.statistical.pValue.toFixed(4)} and ${Math.abs(comparison.improvement).toFixed(1)}% improvement.`,
        confidence: Math.round((1 - comparison.statistical.pValue) * 100) / 100,
        actionable: true,
        impact: 'high',
        category: 'winner_analysis',
        recommendations: [
          'Consider implementing the winning variant',
          'Monitor post-implementation performance',
          'Document learnings for future tests'
        ]
      });
    }

    // Effect size insight
    if (comparison.statistical.effectSize > 0.5) {
      insights.push({
        id: 'large-effect-size',
        type: 'statistical',
        title: 'Large Effect Size Detected',
        description: `The effect size (${comparison.statistical.effectSize.toFixed(2)}) indicates a practically significant difference between variants.`,
        confidence: 0.85,
        actionable: true,
        impact: 'high',
        category: 'winner_analysis',
        data: { effectSize: comparison.statistical.effectSize }
      });
    }

    // Sample size adequacy
    if (comparison.statistical.sampleSizeAdequacy === 'insufficient') {
      insights.push({
        id: 'sample-size-warning',
        type: 'statistical',
        title: 'Insufficient Sample Size',
        description: 'Current sample size may not provide reliable results. Consider extending the test or increasing traffic allocation.',
        confidence: 0.9,
        actionable: true,
        impact: 'medium',
        category: 'test_optimization',
        recommendations: [
          'Extend test duration',
          'Increase traffic allocation',
          'Consider broader audience targeting'
        ]
      });
    }

    return insights;
  };

  const generateTestInsights = (test: ABTest): Insight[] => {
    const insights: Insight[] = [];

    // Test element analysis
    const highPriorityElements = test.testableElements.filter(el => el.priority === 'high');
    if (highPriorityElements.length > 0) {
      insights.push({
        id: 'high-priority-elements',
        type: 'performance',
        title: 'High-Impact Elements Identified',
        description: `${highPriorityElements.length} high-priority elements detected: ${highPriorityElements.map(el => el.elementName).join(', ')}.`,
        confidence: 0.8,
        actionable: true,
        impact: 'high',
        category: 'test_optimization',
        data: { elements: highPriorityElements },
        recommendations: [
          'Focus optimization efforts on these elements',
          'Consider A/B testing variations of these elements',
          'Monitor performance impact closely'
        ]
      });
    }

    // Test duration insight
    if (test.startedAt) {
      const testDuration = Date.now() - test.startedAt.getTime();
      const daysDuration = testDuration / (1000 * 60 * 60 * 24);
      
      if (daysDuration < 3) {
        insights.push({
          id: 'short-test-duration',
          type: 'temporal',
          title: 'Short Test Duration',
          description: `Test has been running for only ${daysDuration.toFixed(1)} days. Consider running longer for more reliable results.`,
          confidence: 0.7,
          actionable: true,
          impact: 'medium',
          category: 'test_optimization',
          recommendations: [
            'Extend test to at least 7 days',
            'Account for weekly patterns',
            'Ensure sufficient sample size'
          ]
        });
      }
    }

    return insights;
  };

  const generateHistoricalInsights = (history: ABTestHistory): Insight[] => {
    const insights: Insight[] = [];

    // Success rate analysis
    const completedTests = history.tests.filter(t => t.status === 'completed');
    const successfulTests = completedTests.filter(t => t.primaryMetricImprovement > 0);
    const successRate = completedTests.length > 0 ? successfulTests.length / completedTests.length : 0;

    if (successRate > 0.7) {
      insights.push({
        id: 'high-success-rate',
        type: 'pattern',
        title: 'High Test Success Rate',
        description: `${Math.round(successRate * 100)}% of your tests show positive results, indicating effective testing strategy.`,
        confidence: 0.85,
        actionable: false,
        impact: 'medium',
        category: 'future_strategy',
        data: { successRate, totalTests: completedTests.length }
      });
    }

    // Pattern recommendations
    if (history.recommendations.length > 0) {
      const topRecommendation = history.recommendations[0];
      insights.push({
        id: 'historical-recommendation',
        type: 'recommendation',
        title: topRecommendation.title,
        description: topRecommendation.description,
        confidence: topRecommendation.confidence / 100,
        actionable: true,
        impact: topRecommendation.expectedImprovement > 15 ? 'high' : 'medium',
        category: 'future_strategy',
        data: topRecommendation,
        recommendations: [`Expected improvement: ${topRecommendation.expectedImprovement}%`]
      });
    }

    return insights;
  };

  const generateBusinessInsights = (test: ABTest, comparison?: ComparisonResult): Insight[] => {
    const insights: Insight[] = [];

    // Revenue impact insight
    if (comparison?.businessImpact.revenueImpact) {
      const impact = comparison.businessImpact.revenueImpact;
      insights.push({
        id: 'revenue-impact',
        type: 'business',
        title: `Revenue Impact: ${impact > 0 ? '+' : ''}$${Math.abs(impact).toLocaleString()}`,
        description: `Implementing the ${comparison.improvement > 0 ? 'winning variant' : 'control'} could ${impact > 0 ? 'increase' : 'decrease'} monthly revenue by $${Math.abs(impact).toLocaleString()}.`,
        confidence: comparison.businessImpact.confidenceLevel / 100,
        actionable: true,
        impact: Math.abs(impact) > 10000 ? 'high' : Math.abs(impact) > 1000 ? 'medium' : 'low',
        category: 'winner_analysis',
        data: { revenueImpact: impact }
      });
    }

    // Template category insight
    const categoryInsight = getCategoryInsight(test.templateName || '', test.metadata?.category || '');
    if (categoryInsight) {
      insights.push(categoryInsight);
    }

    return insights;
  };

  const getCategoryInsight = (templateName: string, category: string): Insight | null => {
    // Generate category-specific insights based on template type
    const categoryInsights: Record<string, Insight> = {
      promotional: {
        id: 'promotional-insight',
        type: 'business',
        title: 'Promotional Email Optimization',
        description: 'Promotional emails typically see 15-25% improvement from CTA button and urgency testing.',
        confidence: 0.75,
        actionable: true,
        impact: 'medium',
        category: 'future_strategy',
        recommendations: [
          'Test urgency-driven subject lines',
          'Experiment with CTA button colors and text',
          'Consider scarcity messaging'
        ]
      },
      newsletter: {
        id: 'newsletter-insight',
        type: 'business',
        title: 'Newsletter Engagement Strategy',
        description: 'Newsletter templates benefit most from personalization and content structure optimization.',
        confidence: 0.8,
        actionable: true,
        impact: 'medium',
        category: 'future_strategy',
        recommendations: [
          'Test personalized subject lines',
          'Experiment with content hierarchy',
          'Consider send time optimization'
        ]
      }
    };

    return categoryInsights[category] || null;
  };

  const analyzePatterns = (history: ABTestHistory): PatternAnalysis => {
    // Analyze winning and failing patterns
    const winningElements = history.patterns.map(pattern => ({
      element: pattern.pattern,
      successRate: pattern.successRate,
      averageImprovement: pattern.averageImprovement,
      frequency: pattern.frequency
    }));

    // Mock failing elements (in real app, would analyze actual failure patterns)
    const failingElements = [
      {
        element: 'Complex headlines',
        failureRate: 0.7,
        averageDecline: -8.5,
        frequency: 3
      }
    ];

    // Mock seasonal trends
    const seasonalTrends = [
      { period: 'Holiday Season', performance: 23.5, confidence: 0.85 },
      { period: 'Summer', performance: 15.2, confidence: 0.75 }
    ];

    // Mock audience insights
    const audienceInsights = [
      { segment: 'Mobile Users', preference: 'Short, action-oriented CTAs', strength: 0.8 },
      { segment: 'Desktop Users', preference: 'Detailed product information', strength: 0.7 }
    ];

    return {
      winningElements,
      failingElements,
      seasonalTrends,
      audienceInsights
    };
  };

  // Filter insights by category
  const filteredInsights = useMemo(() => {
    if (selectedCategory === 'all') return insights;
    return insights.filter(insight => insight.category === selectedCategory);
  }, [insights, selectedCategory]);

  const getInsightIcon = (type: Insight['type']) => {
    switch (type) {
      case 'performance':
        return (
          <svg className="w-5 h-5 text-green-600 dark:text-green-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
          </svg>
        );
      case 'statistical':
        return (
          <svg className="w-5 h-5 text-blue-600 dark:text-blue-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
          </svg>
        );
      case 'business':
        return (
          <svg className="w-5 h-5 text-purple-600 dark:text-purple-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
          </svg>
        );
      case 'pattern':
        return (
          <svg className="w-5 h-5 text-orange-600 dark:text-orange-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
          </svg>
        );
      default:
        return (
          <svg className="w-5 h-5 text-content-secondary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        );
    }
  };

  const getImpactColor = (impact: Insight['impact']) => {
    switch (impact) {
      case 'high': return 'text-red-600 dark:text-red-300 bg-red-50 dark:bg-red-900/30';
      case 'medium': return 'text-yellow-600 dark:text-yellow-300 bg-yellow-50 dark:bg-yellow-900/30';
      case 'low': return 'text-green-600 bg-green-50';
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-content">AI Insights Engine</h2>
          <p className="text-content-secondary mt-1">Automated analysis and recommendations for your A/B test</p>
        </div>
        
        {isAnalyzing && (
          <div className="flex items-center space-x-2 text-blue-600 dark:text-blue-300">
            <svg className="w-5 h-5 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
            <span className="text-sm">Analyzing...</span>
          </div>
        )}
      </div>

      {/* Category Filter */}
      <div className="flex space-x-2 overflow-x-auto pb-2">
        {[
          { id: 'all', label: 'All Insights' },
          { id: 'winner_analysis', label: 'Winner Analysis' },
          { id: 'test_optimization', label: 'Test Optimization' },
          { id: 'future_strategy', label: 'Future Strategy' },
          { id: 'risk_assessment', label: 'Risk Assessment' }
        ].map((category) => (
          <button
            key={category.id}
            onClick={() => setSelectedCategory(category.id as any)}
            className={`px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-colors ${
              selectedCategory === category.id
                ? 'bg-blue-600 text-white'
                : 'bg-gray-100 dark:bg-dark-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-dark-600'
            }`}
          >
            {category.label}
          </button>
        ))}
      </div>

      {/* Insights List */}
      <div className="space-y-4">
        {filteredInsights.length === 0 ? (
          <div className="text-center py-8">
            <svg className="w-12 h-12 text-content-subtle mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
            </svg>
            <p className="text-content-secondary">No insights available for the selected category.</p>
          </div>
        ) : (
          filteredInsights.map((insight) => (
            <div key={insight.id} className="bg-surface rounded-lg shadow-sm border border-line p-6">
              <div className="flex items-start space-x-4">
                <div className="flex-shrink-0 mt-1">
                  {getInsightIcon(insight.type)}
                </div>
                
                <div className="flex-1">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <h3 className="text-lg font-medium text-content mb-2">
                        {insight.title}
                      </h3>
                      <p className="text-gray-700 dark:text-gray-300 mb-3">
                        {insight.description}
                      </p>
                    </div>
                    
                    <div className="flex items-center space-x-2 ml-4">
                      <span className={`px-2 py-1 rounded text-xs font-medium ${getImpactColor(insight.impact)}`}>
                        {insight.impact} impact
                      </span>
                      <span className="text-sm text-content-secondary">
                        {Math.round(insight.confidence * 100)}% confidence
                      </span>
                    </div>
                  </div>

                  {/* Recommendations */}
                  {insight.recommendations && insight.recommendations.length > 0 && (
                    <div className="mt-4">
                      <h4 className="text-sm font-medium text-content mb-2">Recommendations:</h4>
                      <ul className="space-y-1">
                        {insight.recommendations.map((rec, index) => (
                          <li key={index} className="flex items-start space-x-2 text-sm text-content-secondary">
                            <span className="text-blue-500 dark:text-blue-400 mt-1">•</span>
                            <span>{rec}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* Action button for actionable insights */}
                  {insight.actionable && (
                    <div className="mt-4">
                      <button className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors">
                        Take Action
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Pattern Analysis */}
      {patternAnalysis && (
        <div className="bg-surface rounded-lg shadow-sm border border-line p-6">
          <h3 className="text-lg font-semibold text-content mb-4">Pattern Analysis</h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Winning Elements */}
            <div>
              <h4 className="text-sm font-medium text-content mb-3">Winning Elements</h4>
              <div className="space-y-2">
                {patternAnalysis.winningElements.map((element, index) => (
                  <div key={index} className="flex items-center justify-between p-3 bg-green-50 dark:bg-green-900/30 rounded-lg">
                    <span className="text-sm font-medium text-content">{element.element}</span>
                    <div className="text-right">
                      <div className="text-sm font-medium text-green-600 dark:text-green-300">
                        +{element.averageImprovement.toFixed(1)}%
                      </div>
                      <div className="text-xs text-content-secondary">
                        {Math.round(element.successRate * 100)}% success rate
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Audience Insights */}
            <div>
              <h4 className="text-sm font-medium text-content mb-3">Audience Insights</h4>
              <div className="space-y-2">
                {patternAnalysis.audienceInsights.map((insight, index) => (
                  <div key={index} className="p-3 bg-blue-50 dark:bg-blue-900/30 rounded-lg">
                    <div className="text-sm font-medium text-content">{insight.segment}</div>
                    <div className="text-sm text-gray-700 dark:text-gray-300 mt-1">{insight.preference}</div>
                    <div className="text-xs text-blue-600 dark:text-blue-300 mt-1">
                      {Math.round(insight.strength * 100)}% confidence
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default InsightsEngine;